import "./App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { WagmiProvider } from "wagmi";
import { QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer, Bounce } from "react-toastify";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { WalletViewProvider } from "./futurex/contexts/WalletViewContext";
import { wagmiConfig, queryClient } from "./futurex/wagmiConfig";
import { RainbowKitProvider, lightTheme, darkTheme } from "@rainbow-me/rainbowkit";
import "@rainbow-me/rainbowkit/styles.css";
import Layout from "./components/Layout";
import { captureReferral } from "./futurex/lib/referral";

/* Remember ?ref=0x… from the landing URL before any route change drops it */
captureReferral();

/* Lazy-load each page so the initial bundle stays small */
const MarketsPage = lazy(() => import("./futurex/pages/MarketsPage"));
const MarketDetailPage = lazy(() => import("./futurex/pages/MarketDetailPage"));
const PortfolioPage = lazy(() => import("./futurex/pages/PortfolioPage"));
const AffiliatePage = lazy(() => import("./futurex/pages/AffiliatePage"));
const LeaderboardPage = lazy(() => import("./futurex/pages/LeaderboardPage"));
const AdminPage = lazy(() => import("./futurex/pages/AdminPage"));

/* Page-level loading shell — keeps the orb backdrop visible while JS loads */
const PageFallback = () => (
    <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-usdx-accent border-t-transparent rounded-full animate-spin" />
    </div>
);

/* ToastContainer themed to the active colour palette */
const ThemedToasts = () => {
    const { theme } = useTheme();
    return (
        <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover
            theme={theme}
            transition={Bounce}
        />
    );
};

/* Wallet modal, themed to match the app's light / dark mode */
const RainbowKitThemed = ({ children }) => {
    const { theme } = useTheme();
    const rkTheme = theme === "dark"
        ? darkTheme({ accentColor: "#5CACF2", accentColorForeground: "#0A1626", borderRadius: "large" })
        : lightTheme({ accentColor: "#066FD2", accentColorForeground: "#FFFFFF", borderRadius: "large" });
    return (
        <RainbowKitProvider theme={rkTheme} modalSize="compact">
            {children}
        </RainbowKitProvider>
    );
};

function App() {
    return (
        <WagmiProvider config={wagmiConfig}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider>
                    <RainbowKitThemed>
                    <WalletViewProvider>
                        <div className="App">
                            <BrowserRouter>
                                <Suspense fallback={<PageFallback />}>
                                    <Routes>
                                        {/* Markets listing is the landing page */}
                                        <Route
                                            path="/"
                                            element={
                                                <Layout>
                                                    <MarketsPage />
                                                </Layout>
                                            }
                                        />
                                        <Route
                                            path="/markets/:id"
                                            element={
                                                <Layout>
                                                    <MarketDetailPage />
                                                </Layout>
                                            }
                                        />
                                        <Route
                                            path="/portfolio"
                                            element={
                                                <Layout>
                                                    <PortfolioPage />
                                                </Layout>
                                            }
                                        />
                                        <Route
                                            path="/affiliate"
                                            element={
                                                <Layout>
                                                    <AffiliatePage />
                                                </Layout>
                                            }
                                        />
                                        <Route
                                            path="/leaderboard"
                                            element={
                                                <Layout>
                                                    <LeaderboardPage />
                                                </Layout>
                                            }
                                        />
                                        {/* Admin pages are nested — AdminPage renders its own <Routes> */}
                                        <Route
                                            path="/admin/*"
                                            element={
                                                <Layout>
                                                    <AdminPage />
                                                </Layout>
                                            }
                                        />
                                        {/* Catch-all */}
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </Suspense>
                            </BrowserRouter>
                            <ThemedToasts />
                        </div>
                    </WalletViewProvider>
                    </RainbowKitThemed>
                </ThemeProvider>
            </QueryClientProvider>
        </WagmiProvider>
    );
}

export default App;
